import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _isArray from "lodash/isArray";
var _excluded = ["className", "disabled", "error", "errorMessage", "errorMessageId", "feedbackMessages", "fieldset", "help", "helpMessage", "helpMessageId", "input", "inputId", "fullWidth", "label", "tooltip", "tooltipPlacement"];

var _FEEDBACK_COLOR_MAP, _FEEDBACK_ICON_MAP;

import "core-js/modules/es6.array.map.js";
import React, { useEffect } from 'react';
import classNames from 'classnames';
import warning from 'warning';
import classes from '@nerdwallet/base-styles/classes';
import { useDeprecatedPropsWarning } from '@nerdwallet/deprecate';
import ErrorIcon from '@nerdwallet/react-icon/nerdwallet-ui/Error';
import FeedbackSuccessIcon from '@nerdwallet/react-icon/nerdwallet-ui/FeedbackSuccess';
import { useDeprecatedClassNameStylePropsWarning } from '../../internal/deprecated-classname-style-props';
import { Tooltip } from '../Tooltip/Tooltip';
import { Type } from '../Type/Type';
import { Bullet } from './BulletIcon';
import styles from './InputGroup.module.less';
export var FEEDBACK_TYPES = {
  ERROR: 'error',
  INFO: 'info',
  SUCCESS: 'success'
};
var FEEDBACK_COLOR_MAP = (_FEEDBACK_COLOR_MAP = {}, _defineProperty(_FEEDBACK_COLOR_MAP, FEEDBACK_TYPES.ERROR, 'red-dark'), _defineProperty(_FEEDBACK_COLOR_MAP, FEEDBACK_TYPES.INFO, 'neutral-dark'), _defineProperty(_FEEDBACK_COLOR_MAP, FEEDBACK_TYPES.SUCCESS, 'green-dark'), _FEEDBACK_COLOR_MAP);
var FEEDBACK_ICON_MAP = (_FEEDBACK_ICON_MAP = {}, _defineProperty(_FEEDBACK_ICON_MAP, FEEDBACK_TYPES.ERROR, ErrorIcon), _defineProperty(_FEEDBACK_ICON_MAP, FEEDBACK_TYPES.INFO, Bullet), _defineProperty(_FEEDBACK_ICON_MAP, FEEDBACK_TYPES.SUCCESS, FeedbackSuccessIcon), _FEEDBACK_ICON_MAP);
var fieldsetResetClassName = classNames([styles['min-width-0'], classes('border-all-0')]);
var legendResetClassName = classNames([classes('width-100')]);
/**
 * Determines if a component has any props that should trigger an InputGroup
 * to be rendered. Useful for components that want to conditionally wrap
 * themselves in an InputGroup if any InputGroup props are provided.
 */

export var shouldRenderInputGroup = function shouldRenderInputGroup(props) {
  return ['label', 'helpMessage', 'errorMessage', 'tooltip', 'feedback'].some(function (prop) {
    return props[prop] != null && (!_isArray(props[prop]) || props[prop].length);
  });
};
/**
 * Determines if the InputGroup is composing an Input,
 * or is being used as an InputGroup directly. Useful to set
 * display: inline-block on the outer <label> of an Input
 * component.
 */

var shouldDisplayInlineBlock = function shouldDisplayInlineBlock(children) {
  return React.Children.toArray(children).some(function (node) {
    var _node$type;

    return /*#__PURE__*/React.isValidElement(node) && ( // @ts-expect-error -- The types don't reflect that the `displayName`
    // property exists.
    node.props.component === 'input' || ((_node$type = node.type) === null || _node$type === void 0 ? void 0 : _node$type.displayName) === 'Input');
  });
};
/**
 * Look for any children being used that have a component definition that set
 * the `shouldUseFieldset` hint. This tells InputGroup that it should render
 * using <fieldset> and <legend> instead of <label>. For example, this is set
 * by CheckboxGroup (@nerdwallet/react-checkbox).
 */


var childrenShouldUseFieldset = function childrenShouldUseFieldset(children) {
  return React.Children.toArray(children).some( // @ts-expect-error -- `shouldUseFieldset` is a special, Currency-specific
  // property that isn't reflected in the types.
  function (node) {
    var _node$type2;

    return /*#__PURE__*/React.isValidElement(node) && ((_node$type2 = node.type) === null || _node$type2 === void 0 ? void 0 : _node$type2.shouldUseFieldset);
  });
}; // TODO: we should drop these in v3


var useInvalidPropsWarning = function useInvalidPropsWarning(_ref) {
  var error = _ref.error,
      errorMessage = _ref.errorMessage,
      help = _ref.help,
      tooltip = _ref.tooltip,
      inputId = _ref.inputId;
  useEffect(function () {
    if (error) {
      warning(error && (errorMessage || help), "If using the error prop, provide an errorMessage to indicate what the error is.");
      warning(errorMessage && error, "If using the errorMessage prop, use the error prop as well to indicate an error exists.");
    }

    warning(!(tooltip && !inputId), "If using a tooltip with an input you also need to send an inputId");
  }, [error, errorMessage, help, tooltip, inputId]);
};

/**
 * `InputGroup` renders the given input wrapped with proper spacing and support
 * for a label, help text, tooltip, and error state.
 */
export var InputGroup = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var className = props.className,
      disabled = props.disabled,
      error = props.error,
      errorMessage = props.errorMessage,
      errorMessageId = props.errorMessageId,
      feedbackMessages = props.feedbackMessages,
      fieldset = props.fieldset,
      help = props.help,
      helpMessage = props.helpMessage,
      helpMessageId = props.helpMessageId,
      input = props.input,
      inputId = props.inputId,
      fullWidth = props.fullWidth,
      label = props.label,
      tooltip = props.tooltip,
      tooltipPlacement = props.tooltipPlacement,
      otherProps = _objectWithoutProperties(props, _excluded);

  useDeprecatedPropsWarning({
    component: 'InputGroup',
    deprecatedProps: [{
      version: '1.4.0',
      deprecated: 'help',
      migration: 'Use helpMessage instead.'
    }],
    props: props
  });
  useInvalidPropsWarning(props);
  useDeprecatedClassNameStylePropsWarning('InputGroup', {
    className: className
  });
  /**
   * For convenience, an InputGroup will skip rendering any additional markup
   * if no relevant props are provided.
   */

  if (!shouldRenderInputGroup(props)) {
    return input;
  }

  var shouldUseFieldset = fieldset !== null ? fieldset : childrenShouldUseFieldset(input);
  var ContainerComponent = shouldUseFieldset ? 'fieldset' : 'label';
  var LabelComponent = shouldUseFieldset ? 'legend' : 'span';
  /**
   * Renders a red error message if either component has
   * 1) truthy error and errorMessage props or
   * 2) truthy error and help (deprecated) is provided
   *
   * Adds additional margin-top if a help message isn't set.
   */

  var renderErrorMessage = function renderErrorMessage() {
    if (!error) return null;
    var deprecatedError = error && help ? help : null;
    return (errorMessage || deprecatedError) && /*#__PURE__*/React.createElement(Type // This ensures that the message is not read as part of the input's
    // label when the input is being announced. This does not prevent
    // the screen reader from reading it when it's referenced via another
    // aria attribute, like `aria-describedby`.
    , {
      "aria-hidden": "true",
      className: classNames([classes('display-flex'), _defineProperty({}, classes('margin-top-1'), !helpMessage)]),
      error: true,
      size: 1,
      id: errorMessageId
    }, /*#__PURE__*/React.createElement(ErrorIcon, {
      className: classNames(classes('margin-right-1', 'margin-top-1'), styles['flex-shrink-0'])
    }), ' ', errorMessage || deprecatedError);
  };
  /**
   * Renders a gray help message if either
   * 1) helpMessage is provided
   * 2) help (deprecated) is provided if not used in conjunction with the error prop
   */


  var renderHelpMessage = function renderHelpMessage() {
    // The 'help' message in the deprecated path is actually the error message - don't render twice
    if (!(help || helpMessage) || help && error) return null;
    return /*#__PURE__*/React.createElement(Type // This ensures that the message is not read as part of the input's
    // label when the input is being announced. This does not prevent
    // the screen reader from reading it when it's referenced via another
    // aria attribute, like `aria-describedby`.
    , {
      "aria-hidden": "true",
      className: classes('margin-top-1'),
      color: "neutral-darkest",
      size: 1,
      id: helpMessageId
    }, helpMessage || help);
  };

  var renderFeedback = function renderFeedback() {
    if (!feedbackMessages || !_isArray(feedbackMessages)) return null;
    return feedbackMessages.map(function (f) {
      var IconComponent = FEEDBACK_ICON_MAP[f.feedbackType];
      return /*#__PURE__*/React.createElement(Type // This ensures that the message is not read as part of the input's
      // label when the input is being announced. This does not prevent
      // the screen reader from reading it when it's referenced via another
      // aria attribute, like `aria-describedby`.
      , {
        "aria-hidden": "true",
        className: classes('margin-top-1', 'display-flex'),
        color: FEEDBACK_COLOR_MAP[f.feedbackType],
        size: 1,
        id: f.id,
        key: f.message
      }, /*#__PURE__*/React.createElement(IconComponent, {
        className: classNames(classes('margin-right-1', 'margin-top-1'), styles['flex-shrink-0'])
      }), f.message);
    });
  };

  return /*#__PURE__*/React.createElement(ContainerComponent, _extends({
    className: classNames([className, fullWidth ? classes('width-100') : null, ContainerComponent === 'fieldset' ? fieldsetResetClassName : null, // Since an element that is the labeled control of a
    // <label> and is matching :hover will trigger a hover style, we
    // want to set display: inline-block on the outer
    // <label> to minimize the mouse target area.
    shouldDisplayInlineBlock(input) ? classes('display-inline-block') : classes('display-block')]),
    ref: ref,
    htmlFor: inputId
  }, otherProps), (label || tooltip) && /*#__PURE__*/React.createElement(Type, {
    component: LabelComponent,
    className: classNames([classes('margin-bottom-1', 'display-block'), disabled ? classes('opacity-50') : null, LabelComponent === 'legend' ? legendResetClassName : null]),
    size: 1
  }, tooltip ? /*#__PURE__*/React.createElement(Tooltip, {
    label: label,
    title: "More information about ".concat(label),
    content: tooltip,
    placement: tooltipPlacement !== null && tooltipPlacement !== void 0 ? tooltipPlacement : 'top'
  }) : label), input, renderHelpMessage(), renderErrorMessage(), renderFeedback());
});
InputGroup.displayName = 'InputGroup';